import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, skip } from 'rxjs/operators';

import { BreadcrumbService, HttpService, FilterService /*, UserHandlerService*/, NotificationService, UrlStoreService, BusinessService } from '../../shared/services';
// import { SettledTransactionsStore } from '../../store/index';
import { MONTH } from '../../constants';
import { CompanySuspendedService } from 'app/pages/company-suspended/company-suspended.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { LoginModalComponent } from 'app/pages/company-suspended/login-modal/login-modal.component';
import { dateAdd } from 'app/common-functions';

const STAT_ENDPOINT = 'transactionStat';

@Component({
	selector: 'home-cmp',
	templateUrl: 'home.component.html',
})
export class HomeComponent implements OnInit, OnDestroy {
	id = 'chart1';
	width = 600;
	height = 400;
	type = 'column2d';
	dataFormat = 'json';
	loading: boolean = false;
	
	// 用于管理订阅生命周期
	private destroy$: Subject<void> = new Subject<void>();

	displayData: string = 'amount';
	chartLabel: any[] = [];
	chartData: any[] = [];
	transactionRange: string = '';
	totalTransactionArrayForThreeMonths: {} = {};
	last3MonthsList: any[] = [];
	selectMonth: string = "0";
	showLoginModal: boolean = false;
	bsModalRef: BsModalRef | undefined;

	constructor(
		private breadcrumbService: BreadcrumbService,
		public urlStoreService: UrlStoreService,
		private router: Router,
		private httpService: HttpService,
		public businessService: BusinessService,
		public companySuspendService: CompanySuspendedService,
		private modalService: BsModalService
	) {
		this.urlStoreService.previousUrl = this.urlStoreService.currentUrl;
		this.urlStoreService.currentUrl = router.url;
		
		// 优化：使用 takeUntil 防止内存泄漏
		this.companySuspendService.dueBillingDate.pipe(
			takeUntil(this.destroy$)
		).subscribe((dueDate) => {
			if (!dueDate) return;
			
			const now = new Date().getTime();
			const dueD = dateAdd(new Date(dueDate), 'day', -30);
			const dueTime = dueD.getTime();
			
			if (now > dueTime) {
				this.bsModalRef = this.modalService.show(LoginModalComponent, Object.assign({}, { class: 'billing-modal-max-width' }));
			}
		});
	}

	public barChartOptions: any = {
		scaleShowVerticalLines: false,
		responsive: true,
		scales: {
			xAxes: [
				{
					gridLines: {
						color: '#fff',
					},
					ticks: {
						fontSize: 10,
					},
				},
			],
			yAxes: [
				{
					ticks: {
						beginAtZero: true,
						fontSize: 10,
						callback: (value: any) => {
							return this.convertNumericalValueToStringSeparated(value);
						},
					},
				},
			],
		},
		tooltips: {
			callbacks: {
				label: (tooltipItem: any, data: any) => {
					if (!data || !data.datasets || data.datasets.length === 0) {
						return '';
					}
					const datasetLabel = data.datasets[0].label;
					const yLabel = tooltipItem.yLabel;
					
					if (datasetLabel === 'amount') {
						const formattedValue = this.convertNumericalValueToStringSeparated(Number(yLabel).toFixed(2));
						return 'Amount: $ ' + formattedValue;
					}
					return 'Count: ' + yLabel;
				},
			},
		},
	};

	public barChartLabels: string[] = [];
	public barChartType: string = 'bar';
	public barChartLegend: boolean = false;

	public barChartData: any[] = [
		{
			data: [],
			label: '',
		},
	];

	public lineChartColors: Array<any> = [
		{
			backgroundColor: '#567493',
			borderColor: '#567493',
			pointBackgroundColor: '#567493',
			pointBorderColor: '#fff',
			pointHoverBackgroundColor: '#fff',
			pointHoverBorderColor: 'rgba(148,159,177,0.8)',
		},
	];

	public chartClicked(e: any): void {
		// Handle click event if needed
	}

	public chartHovered(e: any): void {
		// Handle hover event if needed
	}

	ngOnInit(): void {
		this.setBreadCrumbValue();
		
		// 初始化最近三个月列表
		this.initLast3MonthsList();

		// 优化：使用 takeUntil 和 skip
		this.businessService.watchBusinessID$
			.pipe(
				takeUntil(this.destroy$),
				skip(1)
			)
			.subscribe(
				(businessID) => {
					if (businessID && this.transactionRange) {
						this.fetchTransaction();
					}
				},
				(err) => {
					console.error('Error in home component subscription', err);
				}
			);
	}

	private initLast3MonthsList(): void {
		this.last3MonthsList = [];
		let currentDay = new Date();
		// 注意：getUTCMonth 返回 0-11。这里逻辑保持原样，但建议确认是否真的需要 UTC 时间
		currentDay.setMonth(currentDay.getUTCMonth() - 1);
		
		for (let counter = 0; counter < 3; counter++) {
			this.last3MonthsList.push(MONTH[currentDay.getUTCMonth()]);
			currentDay.setMonth(currentDay.getUTCMonth() - 1);
		}
	}

	setBreadCrumbValue(): void {
		const breadCrumb: any = {
			root: 'Home',
			child: '',
			subChild: '',
			subChildLink: null,
			grandChild: null,
			rootList: []
		};
		this.breadcrumbService.setBreadCrumb(breadCrumb);
	}

	setDisplayData(type: string): void {
		this.displayData = type;
		this.fetchTransaction();
	}

	setTransactionRange(range: string): void {
		if (!range) {
			this.resetChartData();
			this.barChartData = [{ data: [], label: '' }];
			this.barChartLabels = [];
			this.transactionRange = '';
			return;
		}

		this.transactionRange = range;	
		if (this.businessService.businessID.getValue()) {
			this.fetchTransaction();
		} else {
			console.warn('No Business ID selected');
		}
	}

	selectionOfLast3Months(): void {
		this.fetchTransaction();
	}

	// 优化：修复精度问题，使用 parseFloat 替代 parseInt
	convertToDollar(val: any): number {
		if (val === null || val === undefined || val === '') return 0;
		return parseFloat(Number(val).toString()) / 100;
	}

	fetchTransaction(): void {
		if (!this.transactionRange) {
			return;
		}
		
		const businessID = this.businessService.businessID.getValue();
		if (!businessID) {
			console.warn('Cannot fetch transaction: No Business ID');
			return;
		}

		this.loading = true;
		let params: any = {
			xBusinessID: businessID,
			noGrid: true,
		};

		switch (this.transactionRange) {
			case 'one_day':
				params.filterType = 'last_24_hrs';
				this.httpService.getAll(STAT_ENDPOINT, params).subscribe(
					(res) => this.processHourlyAggregatedData(res),
					(err) => this.handleFetchError(err, 'one_day')
				);
				break;
			case 'last_7_days':
				params.filterType = 'last_7_days';
				this.httpService.getAll(STAT_ENDPOINT, params).subscribe(
					(res) => this.processAggregatedTransactionData(res, {
						startOffset: -6,
						endOffset: 0,
						labelFormat: 'MM-DD'
					}),
					(err) => this.handleFetchError(err, 'last_7_days')
				);
				break;
			case 'last_30_days':
				params.filterType = 'last_30_days';
				this.httpService.getAll(STAT_ENDPOINT, params).subscribe(
					(res) => this.processAggregatedTransactionData(res, {
						startOffset: -29,
						endOffset: 0,
						labelFormat: 'DD'
					}),
					(err) => this.handleFetchError(err, 'last_30_days')
				);
				break;
			case 'last_60_days':
				params.filterType = 'last_60_days';
				this.httpService.getAll(STAT_ENDPOINT, params).subscribe(
					(res) => this.processAggregatedTransactionData(res, {
						startOffset: -59,
						endOffset: 0,
						labelFormat: 'MM-DD'
					}),
					(err) => this.handleFetchError(err, 'last_60_days')
				);
				break;
			case 'last3Mon':
				// [修改] 判断是否选择了具体月份
				if (this.selectMonth !== null) {					
					this.fetchSpecificMonthData(parseInt(this.selectMonth), params);
				} else {
					// 默认行为：获取最近3个月的聚合数据
					params.filterType = 'last_90_days';
					this.httpService.getAll(STAT_ENDPOINT, params).subscribe(
						(res) => this.processAggregatedTransactionData(res, {
							startOffset: -89,
							endOffset: 0,
							labelFormat: 'MM-DD'
						}),
						(err) => this.handleFetchError(err, 'last3Mon')
					);
				}
				break;
			case 'one_year':
				params.filterType = 'last_12_months';
				this.httpService.getAll(STAT_ENDPOINT, params).subscribe(
					(res) => this.processMonthlyAggregatedData(res),
					(err) => this.handleFetchError(err, 'one_year')
				);
				break;
			default:
				this.loading = false;
				break;
		}
	}

	private handleFetchError(err: any, context: string): void {
		console.error(`Error fetching transaction for ${context}`, err);
		this.loading = false;
	}

	resetChartData(): void {
		this.chartData = [];
		this.chartLabel = [];
	}

	daysInMonth(anyDateInMonth: Date): number {
		return new Date(anyDateInMonth.getFullYear(), anyDateInMonth.getMonth() + 1, 0).getDate();
	}

	private processAggregatedTransactionData(res: any, rangeConfig: {
		startOffset: number;
		endOffset: number;
		labelFormat?: 'MM-DD' | 'DD' | 'MMM';
	}): void {
		this.loading = false;
		this.resetChartData();

		if (!res || !res.data || res.data.length === 0) {
			this.barChartData = [{ data: [], label: this.displayData }];
			this.barChartLabels = [];
			return;
		}

		const backendDataMap = new Map<number, { count: number, amount: number }>();
		res.data.forEach((item: any) => {
			backendDataMap.set(item.sortKey, {
				count: item.count || 0,
				amount: item.totalAmount ? this.convertToDollar(item.totalAmount) : 0
			});
		});

		const labels: string[] = [];
		const data: number[] = [];

		const startDate = new Date();
		startDate.setDate(startDate.getDate() + rangeConfig.startOffset);
		// 重置时间为00:00:00以避免时间部分影响比较，虽然 setDate 通常保留时间，但为了保险起见
		startDate.setHours(0,0,0,0);

		const endDate = new Date();
		endDate.setDate(endDate.getDate() + rangeConfig.endOffset);
		endDate.setHours(0,0,0,0);

		let finalLabelFormat = rangeConfig.labelFormat;

		// 如果跨月且格式为 DD，强制改为 MM-DD
		if (startDate.getMonth() !== endDate.getMonth() && finalLabelFormat === 'DD') {
			finalLabelFormat = 'MM-DD';
		}

		let currentDate = new Date(startDate.getTime());

		while (currentDate <= endDate) {
			const yyyy = currentDate.getFullYear();
			const mm = this.padZero(currentDate.getMonth() + 1);
			const dd = this.padZero(currentDate.getDate());

			const sortKey = parseInt(`${yyyy}${mm}${dd}`, 10);

			let label = '';
			if (finalLabelFormat === 'MM-DD') {
				label = `${mm}-${dd}`;
			} else if (finalLabelFormat === 'DD') {
				label = dd;
			} else if (finalLabelFormat === 'MMM') {
				label = MONTH[currentDate.getMonth()]?.small || mm;
			}

			labels.push(label);

			const record = backendDataMap.get(sortKey);
			if (record) {
				data.push(this.displayData === 'amount' ? record.amount : record.count);
			} else {
				data.push(0);
			}

			currentDate.setDate(currentDate.getDate() + 1);
		}

		this.barChartLabels = labels;
		this.barChartData = [
			{
				data: data,
				label: this.displayData,
			},
		];
	}

	private processHourlyAggregatedData(res: any): void {
		this.loading = false;
		this.resetChartData();

		if (!res || !res.data || res.data.length === 0) {
			this.barChartData = [{ data: [], label: this.displayData }];
			this.barChartLabels = [];
			return;
		}

		// 1. 获取时区信息并计算时差
		const serverOffsetMinutes = res.meta && res.meta.utcOffset !== undefined ? res.meta.utcOffset : 0;
		const browserOffsetMinutes = new Date().getTimezoneOffset();
		
		// 计算服务器时间与本地时间的差值（小时）
		// 注意：getTimezoneOffset 返回的是 UTC-Local 的分钟数，所以 Local = UTC - offset
		// 这里保持原有逻辑：(ServerOffset - BrowserOffset) / 60
		const offsetDiffHours = (serverOffsetMinutes - browserOffsetMinutes) / 60;

		// 初始化本地时间 0-23 小时的数据容器
		const localHourlyData = new Array(24).fill(null).map(() => ({ count: 0, amount: 0 }));

		// 2. 处理数据并映射到本地时间
		res.data.forEach((item: any) => {
			if (item.hour === undefined || item.hour === null) return;

			// item.hour 是服务器时间的小时 (0-23)
			let localHour = item.hour + offsetDiffHours;
			
			// 处理跨天情况，确保结果在 0-23 之间
			localHour = localHour % 24;
			if (localHour < 0) {
				localHour += 24;
			}
			
			const index = Math.floor(localHour);
			if (index >= 0 && index < 24) {
				localHourlyData[index].count += (item.count || 0);
				localHourlyData[index].amount += (item.totalAmount ? this.convertToDollar(item.totalAmount) : 0);
			}
		});

		// 3. 构建图表数据 - 按当前时间动态排序
		// 获取本地当前小时
		const currentLocalHour = new Date().getHours();
		
		// 确定图表最右侧（最新）的小时：当前小时的前一个小时
		// 例如：当前 20:18，则最新完整小时段为 19:00-20:00，即 index 19
		let endHourIndex = currentLocalHour;
		if (endHourIndex < 0) {
			endHourIndex = 23; // 跨天处理，如当前 00:15，则最新为 23:00-00:00
		}

		const labels: string[] = [];
		const data: number[] = [];

		// 我们需要生成一个从 "最早时间" 到 "最晚时间(endHourIndex)" 的序列
		// 总共 24 个点
		// 最晚时间是 endHourIndex
		// 最早时间是 endHourIndex 往前推 23 个小时
		
		for (let i = 0; i < 24; i++) {
			// 计算当前遍历到的位置对应的小时索引
			// i=0 是最左侧（最早），i=23 是最右侧（最晚）
			// 公式：(endHourIndex - 23 + i) 可能会小于0，所以需要模运算处理
			let hourIndex = endHourIndex - 23 + i;
			
			// 标准化到 0-23
			while (hourIndex < 0) {
				hourIndex += 24;
			}
			while (hourIndex >= 24) {
				hourIndex -= 24;
			}

			// 添加标签
			labels.push(this.padZero(hourIndex));

			// 添加数据
			const record = localHourlyData[hourIndex];
			if (record) {
				data.push(this.displayData === 'amount' ? record.amount : record.count);
			} else {
				data.push(0);
			}
		}

		this.barChartLabels = labels;
		this.barChartData = [
			{
				data: data,
				label: this.displayData,
			},
		];
	}

	private processMonthlyAggregatedData(res: any): void {
		this.loading = false;
		this.resetChartData();

		if (!res || !res.data || res.data.length === 0) {
			this.barChartData = [{ data: [], label: this.displayData }];
			this.barChartLabels = [];
			return;
		}

		const backendDataMap = new Map<number, { count: number, amount: number }>();
		res.data.forEach((item: any) => {
			if (item.sortKey) {
				backendDataMap.set(item.sortKey, {
					count: item.count || 0,
					amount: item.totalAmount ? this.convertToDollar(item.totalAmount) : 0
				});
			}
		});

		const labels: string[] = [];
		const data: number[] = [];
		const today = new Date();

		for (let i = 11; i >= 0; i--) {
			const targetDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
			const yyyy = targetDate.getFullYear();
			const mm = this.padZero(targetDate.getMonth() + 1);
			
			const sortKey = parseInt(`${yyyy}${mm}`, 10);
			const monthIndex = targetDate.getMonth();
			const label = MONTH[monthIndex]?.small || mm;

			labels.push(label);

			const record = backendDataMap.get(sortKey);
			if (record) {
				data.push(this.displayData === 'amount' ? record.amount : record.count);
			} else {
				data.push(0);
			}
		}

		this.barChartLabels = labels;
		this.barChartData = [
			{
				data: data,
				label: this.displayData,
			},
		];
		// 同样移除了未定义的 resetDataArray() 调用
	}

	/**
	 * 优化的数字格式化函数，移除 console.log，修复逻辑
	 */
	private convertNumericalValueToStringSeparated(value: any): string {
		if (value === null || value === undefined) return '';
		
		// 转换为数字进行处理
		const num = Number(value);
		if (isNaN(num)) return String(value);

		// 分离整数和小数部分
		const parts = num.toString().split('.');
		let integerPart = parts[0];
		const decimalPart = parts.length > 1 ? '.' + parts[1] : '';

		// 处理负数
		const isNegative = integerPart.startsWith('-');
		if (isNegative) {
			integerPart = integerPart.substring(1);
		}

		// 使用正则表达式添加千分位逗号
		// \B 匹配非单词边界，(?=(\d{3})+(?!\d)) 正向预查，确保后面跟着3的倍数个数字且不再有更多数字
		const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

		return (isNegative ? '-' : '') + formattedInteger + decimalPart;
	}
		/**
	 * [新增] 获取指定月份的数据
	 * @param monthIndex 0 (最近一个月), 1 (前一个月), 2 (前两个月)
	 */
	private fetchSpecificMonthData(monthIndex: number, params: any): void {
		// [修复] 增加边界检查
		if (monthIndex < 0 || monthIndex >= this.last3MonthsList.length) {
			console.warn(`Invalid monthIndex: ${monthIndex}. Expected 0-${this.last3MonthsList.length - 1}.`);
			return;
		}

		console.log('fetchSpecificMonthData', monthIndex);

		const today = new Date();

		// 所以偏移量应该是 -(monthIndex + 1)
		const monthOffset = -(monthIndex + 1);

		// 计算目标日期
		const targetDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);

		console.log('targetDate', targetDate);

		const year = targetDate.getFullYear();
		const month = targetDate.getMonth() + 1; // 1-12

		params.filterType = 'custom_month';
		params.year = year;
		params.month = month;

		this.httpService.getAll(STAT_ENDPOINT, params).subscribe(
			(res) => this.processSingleMonthDailyData(res, year, month),
			(err) => this.handleFetchError(err, `specific_month_${year}_${month}`)
		);
	}

	/**
	 * [新增] 处理单月每日数据
	 * 类似于 processAggregatedTransactionData，但范围固定为某个月的1号到最后一天
	 */
	private processSingleMonthDailyData(res: any, year: number, month: number): void {
		this.loading = false;
		this.resetChartData();

		if (!res || !res.data || res.data.length === 0) {
			this.barChartData = [{ data: [], label: this.displayData }];
			this.barChartLabels = [];
			return;
		}

		const backendDataMap = new Map<number, { count: number, amount: number }>();
		res.data.forEach((item: any) => {
			backendDataMap.set(item.sortKey, {
				count: item.count || 0,
				amount: item.totalAmount ? this.convertToDollar(item.totalAmount) : 0
			});
		});

		const labels: string[] = [];
		const data: number[] = [];

		// 获取该月的天数
		const daysInTargetMonth = new Date(year, month, 0).getDate();

		for (let day = 1; day <= daysInTargetMonth; day++) {
			const currentDate = new Date(year, month - 1, day);
			const yyyy = currentDate.getFullYear();
			const mm = this.padZero(currentDate.getMonth() + 1);
			const dd = this.padZero(currentDate.getDate());
			
			const sortKey = parseInt(`${yyyy}${mm}${dd}`, 10);
			const label = dd; // 单月视图通常只显示日期

			labels.push(label);

			const record = backendDataMap.get(sortKey);
			if (record) {
				data.push(this.displayData === 'amount' ? record.amount : record.count);
			} else {
				data.push(0);
			}
		}

		this.barChartLabels = labels;
		this.barChartData = [
			{
				data: data,
				label: this.displayData,
			},
		];
	}

	// 在 HomeComponent 类中添加
	private padZero(num: number): string {
		return num < 10 ? '0' + num : num.toString();
	}
	ngOnDestroy(): void {
		// 触发取消订阅
		this.destroy$.next();
		this.destroy$.complete();
	}
}